
import React, { useState } from 'react';
import { RiFullscreenLine } from 'react-icons/ri';
import Menu from '../../element/ui/core/menu';
import MenuItem, { MenuItemLabel } from '../../element/ui/core/menu-item';


import { ZOOM_CANVAS } from '../../actions';
import { TOOLS_IDS } from '../../utils/constants';
import { useStore } from '../../hooks';
import toPrecisedFloat from '../../utils/toPrecisedFloat';
import {
  CANVAS_ZOOM_STEP_MULTIPLIER,
  getCanvasFitZoomFactor,
  getCanvasPresetZoomFactor,
} from '../../utils/canvasZoomShortcutFactors';
import { ZOOM_FACTORS_PRESETS } from './Topbar.constants';

const isMacPlatform =
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad|iPod/.test(navigator.platform ?? navigator.userAgent);
const FIT_CANVAS_SHORTCUT = isMacPlatform ? '⌘ 0' : 'Ctrl 0';

const CanvasZooming = () => {
  const {
    dispatch,
    zoom = {},
    toolId,
    feedback,
    t,
    shownImageDimensions,
    resize,
    originalImage,
    adjustments: { crop },
    config: { useZoomPresetsMenu },
  } = useStore();
  const isBlockerError = feedback.duration === 0;
  const [zoomingMenuAnchorEl, setZoomingMenuAnchorEl] = useState(null);
  const saveZoom = (zoomFactor) => {
    dispatch({
      type: ZOOM_CANVAS,
      payload: {
        factor: zoomFactor,
      },
    });
  };

  const zoomIn = () => {
    saveZoom(zoom.factor * CANVAS_ZOOM_STEP_MULTIPLIER);
  };

  const fitCanvas = () => {
    saveZoom(
      getCanvasFitZoomFactor({
        resize,
        crop,
        shownImageDimensions,
      }),
    );
  };

  const zoomOut = () => {
    saveZoom(zoom.factor / CANVAS_ZOOM_STEP_MULTIPLIER);
  };

  const toggleZoomingMenu = (event) => {
    setZoomingMenuAnchorEl(zoomingMenuAnchorEl ? null : event.target);
  };

  const applyZoomFactorPreset = (factor) => {
    if (factor === 'fit') {
      fitCanvas();
      toggleZoomingMenu();
      return;
    }
    const helperFactorToAchieveSelected = getCanvasPresetZoomFactor({
      presetFactor: factor,
      originalImage,
      shownImageDimensions,
    });
    if (helperFactorToAchieveSelected != null) {
      saveZoom(helperFactorToAchieveSelected);
    }
    toggleZoomingMenu();
  };

  const isZoomDisabled = toolId === TOOLS_IDS.CROP || isBlockerError;
  const previewToRealImgFactor =
    originalImage && !resize.width && !resize.height
      ? Math.min(
        (shownImageDimensions.width * zoom.factor) / originalImage.width,
        (shownImageDimensions.height * zoom.factor) / originalImage.height,
      )
      : zoom.factor;

  function BiZoomIn(props) {
    return <svg stroke="currentColor" fill="currentColor" strokeWidth={0} viewBox="0 0 24 24" height="1em" width="1em" {...props}><path d="M11 6L9 6 9 9 6 9 6 11 9 11 9 14 11 14 11 11 14 11 14 9 11 9z" /><path d="M10,2c-4.411,0-8,3.589-8,8s3.589,8,8,8c1.846,0,3.543-0.635,4.897-1.688l4.396,4.396l1.414-1.414l-4.396-4.396 C17.365,13.543,18,11.846,18,10C18,5.589,14.411,2,10,2z M10,16c-3.309,0-6-2.691-6-6s2.691-6,6-6s6,2.691,6,6S13.309,16,10,16z" /></svg>;
  }

  function BiZoomOut(props) {
    return <svg stroke="currentColor" fill="currentColor" strokeWidth={0} viewBox="0 0 24 24" height="1em" width="1em" {...props}><path d="M6 9H14V11H6z" /><path d="M10,18c1.846,0,3.543-0.635,4.897-1.688l4.396,4.396l1.414-1.414l-4.396-4.396C17.365,13.543,18,11.846,18,10 c0-4.411-3.589-8-8-8s-8,3.589-8,8S5.589,18,10,18z M10,4c3.309,0,6,2.691,6,6s-2.691,6-6,6s-6-2.691-6-6S6.691,4,10,4z" /></svg>;
  }

  return (
    <>
      <button
        type="button"
        className="respark_topbar-group-btn respark_topbar-zoom-out-btn"
        onClick={zoomOut}
        title={t('zoomOutTitle')}
        disabled={isZoomDisabled}
      >
        <BiZoomOut size={18} aria-hidden />
      </button>
      <button
        type="button"
        className="respark_topbar-group-btn respark_topbar-zoom-label"
        title={t('toggleZoomMenuTitle')}
        onClick={
          isZoomDisabled
            ? undefined
            : (useZoomPresetsMenu && toggleZoomingMenu) || fitCanvas
        }
        disabled={isZoomDisabled}
      >
        {`${toPrecisedFloat(previewToRealImgFactor * 100, 0)}%`}
      </button>
      <button
        type="button"
        className="respark_topbar-group-btn respark_topbar-zoom-in-btn"
        onClick={zoomIn}
        title={t('zoomInTitle')}
        disabled={isZoomDisabled}
      >
        <BiZoomIn size={18} aria-hidden />
      </button>
      <button
        type="button"
        className="respark_topbar-group-btn respark_topbar-zoom-fit-btn"
        onClick={fitCanvas}
        title={`${t('fitToScreenTitle')} (${FIT_CANVAS_SHORTCUT})`}
        aria-label={t('fitToScreenTitle')}
        disabled={isZoomDisabled}
      >
        <RiFullscreenLine size={18} aria-hidden />
      </button>
      <Menu
        anchorEl={zoomingMenuAnchorEl}
        onClose={toggleZoomingMenu}
        open={Boolean(zoomingMenuAnchorEl)}
        position="bottom"
        className="respark_topbar-zoom-menu"
      >
        {ZOOM_FACTORS_PRESETS.map(({ factor, labelKey, label }) => (
          <MenuItem
            key={label || labelKey}
            onClick={() => applyZoomFactorPreset(factor)}
          >
            <MenuItemLabel>{label ?? t(labelKey)}</MenuItemLabel>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default CanvasZooming;
